// =============================================================================
// VandieZ admin-web -- Business masters (/masterdata/business)
// =============================================================================
// Curation surface for `reference_data` (goods types, body types) and
// `capacity_bands`. AD-16 / C-24.
//
// Migration 0062 created these catalogs so a body type or goods type could be
// added without a release, and 0063/0096 made them authoritative at the API
// boundary -- but nothing could edit them, so the catalog's whole promise
// still required someone to run SQL. This page is that missing surface.
//
// Two things here look like omissions and are not:
//
//   * **No delete.** Other tables store these codes BY VALUE with no foreign
//     key (`vehicles.vehicle_type`, `loads.material_category`, ...), so a
//     delete would strand live rows silently. Retiring refuses new writes and
//     leaves existing rows readable, which is what "remove a body type"
//     actually means once trucks are registered.
//   * **No category picker on create beyond the two tabs.** The server's
//     category set is a closed Literal. A category nothing validates and no
//     form renders would be a catalog with no reader.
//
// The `in_use` count next to each code is the blast radius of retiring it:
// retirement never rewrites the rows already carrying the code.
// =============================================================================

import { AlertCircle, Loader2, Plus } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";

import { masterdataAdminApi } from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { ApiError } from "@/types/auth";
import type {
  CapacityBandAdmin,
  PlatformSetting,
  ReferenceCategory,
  ReferenceItem,
  StatusMasterResponse,
} from "@/types/masterdata";

/// Backend error codes get a friendlier rendering than the raw detail string,
/// except the overlap message which already names the conflicting band and is
/// more useful verbatim.
function friendlyError(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === "REFERENCE_CODE_EXISTS") {
      return e.problem.detail;
    }
    if (e.code === "CAPACITY_BAND_OVERLAP") {
      return e.problem.detail;
    }
    if (e.code === "CAPACITY_BAND_RANGE_INVALID") {
      return "Maximum weight must be at least the minimum.";
    }
    return e.problem.detail;
  }
  return "Unexpected error.";
}

const CATEGORY_LABELS: Record<ReferenceCategory, string> = {
  goods_type: "Goods types",
  body_type: "Body types",
};

const CATEGORY_HINTS: Record<ReferenceCategory, string> = {
  goods_type:
    "What is being shipped. A soft picker catalog -- loads store the value as free text, so retiring a code never invalidates a past load.",
  body_type:
    "The truck's body. Validated at the API boundary: retiring a code refuses new vehicles and loads that ask for it, while existing ones keep working.",
};

// -----------------------------------------------------------------------------
// Reference data
// -----------------------------------------------------------------------------
interface ReferenceTabProps {
  category: ReferenceCategory;
}

function ReferenceTab({ category }: ReferenceTabProps): React.JSX.Element {
  const { accessToken } = useAuth();
  const [rows, setRows] = useState<ReferenceItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState<boolean>(false);

  const [code, setCode] = useState<string>("");
  const [label, setLabel] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<string>("0");
  const [submitting, setSubmitting] = useState<boolean>(false);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (accessToken === null) {
        return;
      }
      try {
        const data = await masterdataAdminApi.listReferenceData(
          accessToken,
          category,
          signal,
        );
        setRows(data);
        setError(null);
      } catch (e) {
        if (signal?.aborted === true) {
          return;
        }
        setError(friendlyError(e));
      }
    },
    [accessToken, category],
  );

  useEffect(() => {
    const controller = new AbortController();
    setRows(null);
    void load(controller.signal);
    return () => {
      controller.abort();
    };
  }, [load]);

  const submit = async () => {
    if (accessToken === null) {
      return;
    }
    if (code.trim().length === 0 || label.trim().length === 0) {
      setError("Code and label are both required.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await masterdataAdminApi.createReferenceItem(accessToken, {
        category,
        code: code.trim(),
        label: label.trim(),
        sort_order: Number.parseInt(sortOrder, 10) || 0,
      });
      setCode("");
      setLabel("");
      setSortOrder("0");
      setShowCreate(false);
      await load();
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (row: ReferenceItem) => {
    if (accessToken === null) {
      return;
    }
    setBusyId(row.id);
    setError(null);
    try {
      await masterdataAdminApi.updateReferenceItem(accessToken, row.id, {
        is_active: !row.is_active,
      });
      await load();
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle>{CATEGORY_LABELS[category]}</CardTitle>
          <CardDescription>{CATEGORY_HINTS[category]}</CardDescription>
        </div>
        <Button
          size="sm"
          onClick={() => {
            setShowCreate((v) => !v);
          }}
        >
          <Plus className="mr-1 h-4 w-4" />
          Add code
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {error !== null && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {showCreate && (
          <div className="grid gap-3 rounded-md border p-4 sm:grid-cols-3">
            <div className="space-y-1">
              <Label htmlFor={`${category}-code`}>Code</Label>
              <Input
                id={`${category}-code`}
                value={code}
                placeholder="flatbed"
                onChange={(e) => {
                  setCode(e.target.value);
                }}
              />
              <p className="text-xs text-muted-foreground">
                Lower case, digits and underscores. This is the identifier
                other records store, and it cannot be changed later.
              </p>
            </div>
            <div className="space-y-1">
              <Label htmlFor={`${category}-label`}>Label</Label>
              <Input
                id={`${category}-label`}
                value={label}
                placeholder="Flatbed"
                onChange={(e) => {
                  setLabel(e.target.value);
                }}
              />
              <p className="text-xs text-muted-foreground">
                What drivers and shippers see. Safe to edit any time.
              </p>
            </div>
            <div className="space-y-1">
              <Label htmlFor={`${category}-sort`}>Sort order</Label>
              <Input
                id={`${category}-sort`}
                value={sortOrder}
                inputMode="numeric"
                onChange={(e) => {
                  setSortOrder(e.target.value);
                }}
              />
            </div>
            <div className="sm:col-span-3">
              <Button disabled={submitting} onClick={() => void submit()}>
                {submitting && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Create
              </Button>
            </div>
          </div>
        )}

        {rows === null ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading…
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No codes in this category yet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-2 pr-4 font-medium">Code</th>
                  <th className="py-2 pr-4 font-medium">Label</th>
                  <th className="py-2 pr-4 font-medium">Sort</th>
                  <th className="py-2 pr-4 font-medium">In use</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-b last:border-0">
                    <td className="py-2 pr-4 font-mono text-xs">{row.code}</td>
                    <td className="py-2 pr-4">{row.label}</td>
                    <td className="py-2 pr-4 tabular-nums">{row.sort_order}</td>
                    <td className="py-2 pr-4 tabular-nums">
                      {row.in_use_count}
                    </td>
                    <td className="py-2 pr-4">
                      {row.is_active ? (
                        <span className="rounded bg-success-subtle px-2 py-0.5 text-xs text-success-subtle-foreground">
                          Active
                        </span>
                      ) : (
                        <span className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                          Retired
                        </span>
                      )}
                    </td>
                    <td className="py-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={busyId === row.id}
                        onClick={() => void toggleActive(row)}
                      >
                        {busyId === row.id && (
                          <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                        )}
                        {row.is_active ? "Retire" : "Reactivate"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-3 text-xs text-muted-foreground">
              Retiring hides a code from every picker and refuses new records
              that ask for it. The <strong>in use</strong> count is how many
              live records already carry it — those keep working and are not
              rewritten.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Capacity bands
// -----------------------------------------------------------------------------
function CapacityBandsCard(): React.JSX.Element {
  const { accessToken } = useAuth();
  const [rows, setRows] = useState<CapacityBandAdmin[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState<boolean>(false);

  const [name, setName] = useState<string>("");
  const [minKg, setMinKg] = useState<string>("");
  const [maxKg, setMaxKg] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<string>("0");
  const [submitting, setSubmitting] = useState<boolean>(false);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (accessToken === null) {
        return;
      }
      try {
        const data = await masterdataAdminApi.listCapacityBands(
          accessToken,
          signal,
        );
        setRows(data);
        setError(null);
      } catch (e) {
        if (signal?.aborted === true) {
          return;
        }
        setError(friendlyError(e));
      }
    },
    [accessToken],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => {
      controller.abort();
    };
  }, [load]);

  const submit = async () => {
    if (accessToken === null) {
      return;
    }
    const min = Number.parseInt(minKg, 10);
    if (name.trim().length === 0 || Number.isNaN(min)) {
      setError("Name and minimum weight are required.");
      return;
    }
    const max = maxKg.trim().length === 0 ? null : Number.parseInt(maxKg, 10);
    setSubmitting(true);
    setError(null);
    try {
      await masterdataAdminApi.createCapacityBand(accessToken, {
        name: name.trim(),
        min_kg: min,
        max_kg: max,
        sort_order: Number.parseInt(sortOrder, 10) || 0,
      });
      setName("");
      setMinKg("");
      setMaxKg("");
      setSortOrder("0");
      setShowCreate(false);
      await load();
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (row: CapacityBandAdmin) => {
    if (accessToken === null) {
      return;
    }
    setBusyId(row.id);
    setError(null);
    try {
      await masterdataAdminApi.updateCapacityBand(accessToken, row.id, {
        is_active: !row.is_active,
      });
      await load();
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle>Capacity bands</CardTitle>
          <CardDescription>
            How a truck&apos;s payload is described to users. Bands must not
            overlap — a weight resolves to the first matching band, so an
            overlap would let the same truck change band after an unrelated
            edit. The server refuses overlapping ranges.
          </CardDescription>
        </div>
        <Button
          size="sm"
          onClick={() => {
            setShowCreate((v) => !v);
          }}
        >
          <Plus className="mr-1 h-4 w-4" />
          Add band
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {error !== null && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {showCreate && (
          <div className="grid gap-3 rounded-md border p-4 sm:grid-cols-4">
            <div className="space-y-1">
              <Label htmlFor="band-name">Name</Label>
              <Input
                id="band-name"
                value={name}
                placeholder="10T"
                onChange={(e) => {
                  setName(e.target.value);
                }}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="band-min">Minimum kg</Label>
              <Input
                id="band-min"
                value={minKg}
                inputMode="numeric"
                onChange={(e) => {
                  setMinKg(e.target.value);
                }}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="band-max">Maximum kg</Label>
              <Input
                id="band-max"
                value={maxKg}
                inputMode="numeric"
                placeholder="leave blank for no limit"
                onChange={(e) => {
                  setMaxKg(e.target.value);
                }}
              />
              <p className="text-xs text-muted-foreground">
                Blank makes this the open-ended top band. Only one band can be
                open-ended, since every heavier band would overlap it.
              </p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="band-sort">Sort order</Label>
              <Input
                id="band-sort"
                value={sortOrder}
                inputMode="numeric"
                onChange={(e) => {
                  setSortOrder(e.target.value);
                }}
              />
            </div>
            <div className="sm:col-span-4">
              <Button disabled={submitting} onClick={() => void submit()}>
                {submitting && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Create
              </Button>
            </div>
          </div>
        )}

        {rows === null ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading…
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-2 pr-4 font-medium">Name</th>
                  <th className="py-2 pr-4 font-medium">Range</th>
                  <th className="py-2 pr-4 font-medium">Sort</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-b last:border-0">
                    <td className="py-2 pr-4">{row.name}</td>
                    <td className="py-2 pr-4 tabular-nums">
                      {row.min_kg.toLocaleString()} –{" "}
                      {row.max_kg === null
                        ? "no limit"
                        : row.max_kg.toLocaleString()}{" "}
                      kg
                    </td>
                    <td className="py-2 pr-4 tabular-nums">{row.sort_order}</td>
                    <td className="py-2 pr-4">
                      {row.is_active ? (
                        <span className="rounded bg-success-subtle px-2 py-0.5 text-xs text-success-subtle-foreground">
                          Active
                        </span>
                      ) : (
                        <span className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                          Retired
                        </span>
                      )}
                    </td>
                    <td className="py-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={busyId === row.id}
                        onClick={() => void toggleActive(row)}
                      >
                        {busyId === row.id && (
                          <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                        )}
                        {row.is_active ? "Retire" : "Reactivate"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// -----------------------------------------------------------------------------
// Platform settings -- AD-09 / AD-18
// -----------------------------------------------------------------------------
// Lives on this page rather than behind its own route because it is the same
// job: the values a load takes when nobody chooses them are master data, and
// a second nav entry for two rows would be filing, not organisation.
//
// There is no "add setting". The key set is fixed by what the backend
// actually READS -- a settings row nothing consults is worse than a
// constant, because an admin changes it, nothing happens, and the screen
// looks broken.
function PlatformSettingsCard(): React.JSX.Element {
  const { accessToken } = useAuth();
  const [rows, setRows] = useState<PlatformSetting[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (accessToken === null) {
        return;
      }
      try {
        const data = await masterdataAdminApi.listSettings(accessToken, signal);
        setRows(data);
        setError(null);
      } catch (e) {
        if (signal?.aborted === true) {
          return;
        }
        setError(friendlyError(e));
      }
    },
    [accessToken],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => {
      controller.abort();
    };
  }, [load]);

  const save = async (row: PlatformSetting, next: unknown) => {
    if (accessToken === null) {
      return;
    }
    setBusyKey(row.key);
    setError(null);
    try {
      await masterdataAdminApi.updateSetting(accessToken, row.key, next);
      await load();
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Platform defaults</CardTitle>
        <CardDescription>
          What a load uses when the person posting it does not choose. An
          explicit choice on a load always wins over these.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error !== null && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {rows === null ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading…
          </div>
        ) : (
          <div className="space-y-4">
            {rows.map((row) => (
              <div key={row.key} className="rounded-md border p-4">
                <p className="font-mono text-xs">{row.key}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {row.description}
                </p>
                <div className="mt-3 flex items-center gap-2">
                  {typeof row.value === "boolean" ? (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={busyKey === row.key}
                      onClick={() => void save(row, !row.value)}
                    >
                      {busyKey === row.key && (
                        <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                      )}
                      {row.value ? "On — turn off" : "Off — turn on"}
                    </Button>
                  ) : (
                    <>
                      <Input
                        className="max-w-[10rem]"
                        inputMode="numeric"
                        aria-label={row.key}
                        value={drafts[row.key] ?? String(row.value)}
                        onChange={(e) => {
                          setDrafts((d) => ({
                            ...d,
                            [row.key]: e.target.value,
                          }));
                        }}
                      />
                      <Button
                        size="sm"
                        disabled={busyKey === row.key}
                        onClick={() => {
                          const raw = drafts[row.key] ?? String(row.value);
                          const parsed = Number.parseInt(raw, 10);
                          // Refuse locally rather than send NaN and let the
                          // server answer with a type error -- the admin
                          // typed the mistake and should see it here.
                          if (Number.isNaN(parsed)) {
                            setError(`${row.key} must be a whole number.`);
                            return;
                          }
                          void save(row, parsed);
                        }}
                      >
                        {busyKey === row.key && (
                          <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                        )}
                        Save
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
            <p className="text-xs text-muted-foreground">
              Changes apply to loads posted from now on. Loads already open
              keep the settings they were created with.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// -----------------------------------------------------------------------------
/// The published status vocabulary (PR-03, p122).
///
/// Sits among the editable masters but is deliberately read-only, and
/// says so on the card: a status gates the trip state machine, DB CHECK
/// constraints, PoD and settlement, so an admin-added value would be a
/// state the platform can enter and never leave.
function StatusMasterCard(): React.JSX.Element {
  const { accessToken } = useAuth();
  const [data, setData] = useState<StatusMasterResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (accessToken === null) {
        return;
      }
      try {
        setData(await masterdataAdminApi.getStatusMaster(accessToken, signal));
        setError(null);
      } catch (e) {
        if (signal?.aborted === true) {
          return;
        }
        setError(friendlyError(e));
      } finally {
        setLoading(false);
      }
    },
    [accessToken],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => {
      controller.abort();
    };
  }, [load]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Status vocabulary</CardTitle>
        <CardDescription>
          Read-only. Every status the platform publishes, what it means,
          and who sets it. Unlike the masters above these are not
          editable — a status drives the trip state machine and
          settlement, so a new one would be a state nothing can leave.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error !== null && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {loading ? (
          <div className="flex items-center gap-2 py-4 text-muted-foreground text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : data === null ? null : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground text-xs uppercase tracking-wide">
                    <th className="py-2 pr-3">Name</th>
                    <th className="py-2 pr-3">Meaning</th>
                    <th className="py-2 pr-3">Set by</th>
                    <th className="py-2 pr-3">Stored as</th>
                  </tr>
                </thead>
                <tbody>
                  {data.load_status_master.map((r) => (
                    <tr key={r.fsd_name} className="border-b last:border-0">
                      <td className="py-2 pr-3 font-medium">{r.fsd_name}</td>
                      <td className="py-2 pr-3 text-muted-foreground">
                        {r.meaning}
                      </td>
                      <td className="py-2 pr-3 text-muted-foreground">
                        {r.updated_by}
                      </td>
                      <td className="py-2 pr-3">
                        {r.code === null ? (
                          // Not a gap: the stage is real but this
                          // platform derives it from bid counts rather
                          // than storing it as a status.
                          <span className="text-muted-foreground text-xs italic">
                            derived, not stored
                          </span>
                        ) : (
                          <span className="font-mono text-xs">
                            {r.entity}.{r.code}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              {(
                [
                  ["Load", data.load_statuses],
                  ["Trip", data.trip_statuses],
                  ["Bid", data.bid_statuses],
                ] as const
              ).map(([label, codes]) => (
                <div key={label}>
                  <h4 className="mb-1 font-medium text-xs uppercase tracking-wide text-muted-foreground">
                    {label} statuses ({codes.length})
                  </h4>
                  <div className="flex flex-wrap gap-1">
                    {codes.map((c) => (
                      <span
                        key={c}
                        className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs"
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export function BusinessMastersPage(): React.JSX.Element {
  const [tab, setTab] = useState<ReferenceCategory>("body_type");

  return (
    <div className="space-y-6">
      <PageHeader
        title="Business masters"
        description="Goods types, body types and capacity bands. Changes take effect immediately — no release needed."
      />

      <div className="flex gap-2">
        {(Object.keys(CATEGORY_LABELS) as ReferenceCategory[]).map((c) => (
          <Button
            key={c}
            variant={tab === c ? "default" : "outline"}
            size="sm"
            onClick={() => {
              setTab(c);
            }}
          >
            {CATEGORY_LABELS[c]}
          </Button>
        ))}
      </div>

      <ReferenceTab category={tab} />
      <CapacityBandsCard />
      <PlatformSettingsCard />
      <StatusMasterCard />
    </div>
  );
}
